/**
 *                  !!!!!!!!!!!!!!!
 *                  !!! WARNING !!!
 *                  !!!!!!!!!!!!!!!
 *
 * do not import statically any amplience-related components,
 * they get imported to all pages, regardless if they are actually needed or not
 * this leads to increased bundle size with unused code
 *
 * @see https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading
 * @see https://g-star.atlassian.net/wiki/spaces/FS/pages/4610326632/Performance+101
 */

import type { ComponentType, ReactElement } from 'react';

import dynamic from 'next/dynamic';
import { Fragment } from 'react';

import { getAmplienceMeta } from './getAmplienceMeta';
import type { Slot } from '~/amplienceTypes/content';
import type { DataAttribute } from '~/amplienceTypes/partials/exported/element-attributes-schema';
import type { LocalizedSlot } from '~/amplienceTypes/schemas/exported/localized-slot-schema';
import type { FitRecommendations as FitRecommendationsProps } from '~/amplienceTypes/schemas/imported/fit-recommendations-schema';
import type { ProductRecommendations as RecommendationsSchema } from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import type { StyliticsRecommendations as StyliticsRecommendationsProps } from '~/amplienceTypes/schemas/imported/stylitics-recommendations-schema';
import { Container, Row, Column } from '~/components/shared/core/styles';
import { GenericErrorBoundary } from '~/components/shared/error/GenericErrorBoundary';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import { removeLegacyModifierClassNames } from '~/components/unmanaged/styles/utils';
import { Section } from '~/components/unmanaged/templates/section/Section';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SchemaComponent = Record<string, ComponentType<any>>;

const UnmanagedBanner = dynamic(
  async () => (await import('~/components/unmanaged/templates/Banner')).Banner
);

const Recommendations = dynamic(
  async () => (await import('~/components/shared/recommendations/Recommendations')).Recommendations
);

const Categories = dynamic(
  async () => (await import('~/components/shared/recommendations/Categories')).Categories
);

const ProductImageGallery = dynamic(
  async () =>
    (await import('~/components/pdp/productDetail/productImageGallery/ProductImageGallery'))
      .ProductImageGallery
);

const ProductInfo = dynamic(
  async () => (await import('~/components/pdp/productInfo/ProductInfo')).ProductInfo
);

const FiberRanking = dynamic(
  async () => (await import('~/components/pdp/fiberRanking/FiberRanking')).FiberRanking
);

const ProductSustainabilityTraceability = dynamic(
  async () =>
    (await import('~/components/pdp/sustainabilityTraceability/ProductSustainabilityTraceability'))
      .ProductSustainabilityTraceability
);

const PDPBanner = dynamic(async () => (await import('~/components/pdp/banner/Banner')).Banner);

const ProductDescriptionCare = dynamic(
  async () =>
    (await import('~/components/pdp/productDescriptionCare/ProductDescriptionCare'))
      .ProductDescriptionCare
);

const ProductSizeFit = dynamic(
  async () => (await import('~/components/pdp/productSizeFit/ProductSizeFit')).ProductSizeFit
);

const ExplodedView = dynamic(
  async () => (await import('~/components/pdp/explodedView/ExplodedView')).ExplodedView
);

const ProductDetailInfo = dynamic(
  async () => (await import('~/components/pdp/productDetail/ProductDetailInfo')).ProductDetailInfo
);

const ProductAccordions = dynamic(
  async () =>
    (await import('~/components/pdp/productAccordions/ProductAccordions')).ProductAccordions
);

const MarketingComponents = dynamic(
  async () =>
    (await import('~/components/pdp/marketingComponents/MarketingComponents')).MarketingComponents
);

const JavascriptCode = dynamic(
  async () => (await import('~/components/unmanaged/external/JavascriptCode')).JavascriptCode
);

const HtmlCode = dynamic(
  async () => (await import('~/components/unmanaged/external/HtmlCode')).HtmlCode
);

const SectionDownloads = dynamic(
  async () => (await import('~/components/unmanaged/templates/SectionDownloads')).SectionDownloads
);

const PageNotFound = dynamic(
  async () => (await import('~/components/shared/pageNotFound/PageNotFound')).PageNotFound
);

const ServerError = dynamic(
  async () => (await import('~/components/shared/serverError/ServerError')).ServerError
);

const ProductDeliveryAndReturns = dynamic(
  async () =>
    (await import('~/components/pdp/deliveryAndReturns/ProductDeliveryAndReturns'))
      .ProductDeliveryAndReturns
);

const FitRecommendations = dynamic(
  async () =>
    (await import('~/components/shared/recommendations/FitRecommendations')).FitRecommendations
);

const StyliticsRecommendations = dynamic(
  async () =>
    (await import('~/components/shared/recommendations/stylitics/StyliticsRecommendations'))
      .StyliticsRecommendations
);

const SectionHighlightStories = dynamic(
  async () => (await import('~/stories/components/SectionHighlightStories')).SectionHighlightStories
);

export const unmanagedComponents: SchemaComponent = {
  'https://ahc.g-star.com/content/html-code.json': HtmlCode,
  'https://ahc.g-star.com/content/javascript-code.json': JavascriptCode,
  'https://ahc.g-star.com/content/simple-video-banner.json': UnmanagedBanner,
  'https://ahc.g-star.com/content/simple-banner.json': UnmanagedBanner,
  'https://ahc.g-star.com/content/banner.json': UnmanagedBanner,
  'https://ahc.g-star.com/content/localized-banner.json': UnmanagedBanner,
  'https://ahc.g-star.com/content/logo-banner.json': UnmanagedBanner,
  'https://ahc.g-star.com/content/hotspot-banner.json': UnmanagedBanner,
  'https://ahc.g-star.com/content/section.json': Section,
  'https://ahc.g-star.com/content/localized-section.json': Section,
  'https://ahc.g-star.com/content/section-downloads.json': SectionDownloads,
  'https://ahc.g-star.com/content/product-recommendations.json': (props: RecommendationsSchema) => (
    <GenericErrorBoundary fallback={<></>}>
      <Recommendations {...props} />
    </GenericErrorBoundary>
  ),
  'https://ahc.g-star.com/content/section-highlight-stories.json': SectionHighlightStories,
};

export const managedComponents: SchemaComponent = {
  // PDP
  'https://ahc.g-star.com/content/product-image-gallery.json': ProductImageGallery,
  'https://ahc.g-star.com/content/product-info.json': ProductInfo,
  'https://ahc.g-star.com/content/product-size-fit.json': ProductSizeFit,
  'https://ahc.g-star.com/content/product-description-care.json': ProductDescriptionCare,
  'https://ahc.g-star.com/content/product-sustainability-traceability.json':
    ProductSustainabilityTraceability,
  'https://ahc.g-star.com/content/product-delivery-and-returns.json': ProductDeliveryAndReturns,
  'https://ahc.g-star.com/content/pdp-banner.json': PDPBanner,
  'https://ahc.g-star.com/content/product-recommendations.json': (props: RecommendationsSchema) => (
    <GenericErrorBoundary fallback={<></>}>
      <Recommendations model="related-products" {...props} />
    </GenericErrorBoundary>
  ),
  'https://ahc.g-star.com/content/product-detail-info.json': ProductDetailInfo,
  'https://ahc.g-star.com/content/product-exploded-view.json': ExplodedView,
  'https://ahc.g-star.com/content/product-fiber-ranking.json': FiberRanking,
  'https://ahc.g-star.com/content/pdp-marketing-components.json': MarketingComponents,
  'https://ahc.g-star.com/content/category-recommendations.json': Categories,
  'https://ahc.g-star.com/content/fit-recommendations.json': (props: FitRecommendationsProps) => (
    <FeatureFlag flag="pdpFitRecommendationsEnabled">
      <FitRecommendations {...props} />
    </FeatureFlag>
  ),
  'https://ahc.g-star.com/content/stylitics-recommendations.json': (
    props: StyliticsRecommendationsProps
  ) => (
    <FeatureFlag flag="styliticsOutfitsRecommendationsEnabled">
      <GenericErrorBoundary fallback={<></>}>
        <StyliticsRecommendations {...props} />
      </GenericErrorBoundary>
    </FeatureFlag>
  ),
  // Others
  'https://ahc.g-star.com/content/global-404.json': PageNotFound,
  'https://ahc.g-star.com/content/global-500.json': ServerError,
};

export { getAmplienceMeta };

const KeyPDPComponents = new Set(['productImageGallery', 'productDetailInfo', 'productAccordions']);

const specialPDPRowClasses = (slot: Slot): string => {
  const isPDPMainSectionRow = slot.slots?.children?.some(child =>
    child?.slots?.children?.some(grandchild => KeyPDPComponents.has(grandchild.name))
  );

  // done intentionally as a switch so it can be extended later if needed
  switch (/* NOSONAR */ true) {
    case isPDPMainSectionRow: {
      return 'cms-pdp-main-section-row';
    }
    default: {
      return '';
    }
  }
};

const specialPDPColumnClasses = (slot: Slot): string => {
  const hasProductDetailInfo = slot.slots?.children?.some(({ name }) =>
    name.includes('productDetailInfo')
  );
  const hasProductAccordions = slot.slots?.children?.some(({ name }) =>
    name.includes('productAccordions')
  );

  const hasProductImageGallery = slot.slots?.children?.some(({ name }) =>
    name.includes('productImageGallery')
  );

  const isPDPDetailsWrapper = hasProductDetailInfo && hasProductAccordions;

  switch (true) {
    case isPDPDetailsWrapper: {
      return 'cms-product-details-wrapper';
    }
    case hasProductImageGallery: {
      return 'cms-product-gallery-wrapper';
    }
    default: {
      return '';
    }
  }
};

export const showComponent = (slot: Slot, key: number, children?: ReactElement[]): ReactElement => {
  const schema = slot.properties?.content?._meta.schema;
  const Component = schema ? managedComponents[schema] : null;

  switch (slot.name) {
    case 'container': {
      return <Container key={key}>{!!children && children}</Container>;
    }
    case 'row': {
      const className = specialPDPRowClasses(slot);

      return (
        <Row className={className} key={key} {...slot.properties}>
          {!!children && children}
        </Row>
      );
    }
    case 'column': {
      const className = specialPDPColumnClasses(slot);

      return (
        <Column key={key} className={className} {...slot.properties}>
          {!!children && children}
        </Column>
      );
    }
    case 'productAccordions': {
      return (
        <ProductAccordions key={key} name={slot.name} {...slot.properties}>
          {!!children && children}
        </ProductAccordions>
      );
    }
    // Will add component names continuously here for example,  case 'productInfo' || 'newsletter'
    case 'productInfo':
    case 'banner':
    case 'recommendations':
    case 'productImageGallery':
    case 'productDetailInfo':
    case 'productExplodedView':
    case 'productSizeFit':
    case 'productDescriptionCare':
    case 'productSustainabilityTraceability':
    case 'productDeliveryAndReturns':
    case 'fiberRanking':
    case 'marketingComponents':
    case 'sectionHighlightStories':
    case '404':
    case '500': {
      return Component ? (
        <Component key={key} {...slot.properties?.content} />
      ) : (
        <Fragment key={key} />
      );
    }
    default:
      return <Fragment key={key} />;
  }
};

export const showLayoutContent = (slots: Slot[] | null | undefined): ReactElement[] => {
  const result: ReactElement[] = [];

  slots?.forEach((slot, i) => {
    if (slot.slots) {
      result.push(showComponent(slot, i, showLayoutContent(slot.slots.children)));
    } else {
      result.push(showComponent(slot, i));
    }
  });

  return result;
};

export const showSlotContent = (slotContent: LocalizedSlot) =>
  slotContent.content?.map((content, i) => {
    const { metaAttributes, contentId, schema } = getAmplienceMeta(content?._meta);
    const Component = schema ? unmanagedComponents[schema] : null;

    const key = contentId && typeof contentId === 'string' ? `${contentId}-${i}` : `${i}`;

    /**
     * TODO: FS-3437 - PLP Moodbanners remove code for suppressing inline styling
     */
    return Component ? (
      <Component {...metaAttributes} key={key} {...removeLegacyModifierClassNames(content)} />
    ) : (
      <Fragment key={key} />
    );
  });

export const getDataAttributeValue = (
  dataAttributes: DataAttribute[] | undefined,
  name: string
): string | undefined =>
  dataAttributes?.find((dataAttribute: DataAttribute) => dataAttribute.name === name)?.value;

export { hasClassName } from './hasClassName';
